import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Users, Eye, EyeOff, Copy } from "lucide-react";
import axios from "axios";

export default function OwnersPage() {
  const { user, API } = useAuth();
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    username: "", password: "", name: "", cognome: "",
    email: "", telefono: "", codice_fiscale: "", indirizzo: "", iban: "",
    sostituto_imposta: false, regime_fiscale: ""
  });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    axios.get(`${API}/users/owners`)
      .then(r => setOwners(r.data))
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  }, [API]);

  const emptyForm = {
    username: "", password: "", name: "", cognome: "",
    email: "", telefono: "", codice_fiscale: "", indirizzo: "", iban: "",
    sostituto_imposta: false, regime_fiscale: ""
  };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setShowPassword(false); setDialogOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({
      username: o.username || "", password: "", name: o.name || "", cognome: o.cognome || "",
      email: o.email || "", telefono: o.telefono || "", codice_fiscale: o.codice_fiscale || "",
      indirizzo: o.indirizzo || "", iban: o.iban || "",
      sostituto_imposta: o.sostituto_imposta || false, regime_fiscale: o.regime_fiscale || ""
    });
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editing && (!form.username || !form.password || !form.name)) {
      toast.error("Username, password e nome sono obbligatori");
      return;
    }
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.username;
        const res = await axios.put(`${API}/owners/${editing.id}`, payload);
        setOwners(prev => prev.map(o => o.id === editing.id ? res.data : o));
        toast.success("Proprietario aggiornato");
      } else {
        const res = await axios.post(`${API}/auth/register`, { ...form, role: "proprietario" });
        setOwners(prev => [...prev, res.data]);
        toast.success("Proprietario creato");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo proprietario?")) return;
    try {
      await axios.delete(`${API}/owners/${id}`);
      setOwners(prev => prev.filter(o => o.id !== id));
      toast.success("Proprietario eliminato");
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const copyCredentials = () => {
    const text = `Username: ${form.username}\nPassword: ${form.password}`;
    navigator.clipboard.writeText(text);
    toast.success("Credenziali copiate negli appunti");
  };

  if (!isAdmin) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="owners-page">
        <p className="text-stone-500">Accesso riservato agli amministratori</p>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-300 border-t-stone-900" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="owners-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">Proprietari</h1>
          <p className="text-stone-500 mt-1 text-sm">{owners.length} proprietari registrati</p>
        </div>
        <Button onClick={openAdd} data-testid="add-owner-button" className="bg-stone-900 hover:bg-stone-800 text-white">
          <UserPlus className="h-4 w-4 mr-2" /> Nuovo Proprietario
        </Button>
      </div>

      {owners.length === 0 ? (
        <Card className="border border-stone-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <Users className="h-12 w-12 text-stone-300 mx-auto mb-4" strokeWidth={1} />
            <p className="text-stone-500">Nessun proprietario registrato</p>
            <Button onClick={openAdd} className="mt-4 bg-stone-900 text-white">Aggiungi il primo proprietario</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-stone-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="text-xs font-medium text-stone-500">Nome</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Username</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Email</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Telefono</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">C.F.</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">IBAN</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Fiscale</TableHead>
                <TableHead className="text-xs w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {owners.map(o => (
                <TableRow key={o.id} data-testid={`owner-row-${o.id}`} className="hover:bg-stone-50 transition-colors">
                  <TableCell className="text-sm font-medium text-stone-900">{o.name} {o.cognome}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] font-mono">{o.username}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-stone-600">{o.email || "-"}</TableCell>
                  <TableCell className="text-sm text-stone-600">{o.telefono || "-"}</TableCell>
                  <TableCell className="text-xs font-mono text-stone-500">{o.codice_fiscale || "-"}</TableCell>
                  <TableCell className="text-xs font-mono text-stone-500 max-w-[160px] truncate">{o.iban || "-"}</TableCell>
                  <TableCell>
                    {o.sostituto_imposta ? (
                      <Badge className="text-[10px] bg-green-50 text-green-700 border-green-200">Sostituto</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-stone-400">Autonomo</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(o)} data-testid={`edit-owner-${o.id}`}>
                        <Pencil className="h-3.5 w-3.5 text-stone-500" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(o.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="owner-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Modifica Proprietario" : "Nuovo Proprietario"}</DialogTitle>
            <DialogDescription className="text-sm text-stone-500">{editing ? "Modifica i dati anagrafici" : "Crea un nuovo account proprietario"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {!editing && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                <p className="text-xs text-amber-800 font-medium mb-2">Credenziali di accesso</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-stone-600">Username *</Label>
                    <Input data-testid="owner-username-input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="mario.rossi" className="mt-1 bg-white" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600">Password *</Label>
                    <div className="relative mt-1">
                      <Input data-testid="owner-password-input" type={showPassword ? "text" : "password"} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Scegli password" className="bg-white pr-8" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                {form.username && form.password && (
                  <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={copyCredentials}>
                    <Copy className="h-3 w-3 mr-1" /> Copia credenziali
                  </Button>
                )}
              </div>
            )}
            {editing && (
              <div>
                <Label className="text-xs text-stone-600">Nuova Password (lascia vuoto per non cambiare)</Label>
                <div className="relative mt-1">
                  <Input type={showPassword ? "text" : "password"} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Nuova password..." className="bg-white pr-8" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-stone-600">Nome *</Label>
                <Input data-testid="owner-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Mario" className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Cognome</Label>
                <Input data-testid="owner-cognome-input" value={form.cognome} onChange={e => setForm({ ...form, cognome: e.target.value })} placeholder="Rossi" className="mt-1 bg-white" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-stone-600">Email</Label>
              <Input data-testid="owner-email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="mario.rossi@esempio.it" className="mt-1 bg-white" />
              <p className="text-[10px] text-stone-400 mt-1">Utilizzata per le notifiche</p>
            </div>
            <div>
              <Label className="text-xs text-stone-600">Telefono</Label>
              <Input value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} placeholder="+39 333 1234567" className="mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-xs text-stone-600">Codice Fiscale</Label>
              <Input value={form.codice_fiscale} onChange={e => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} placeholder="RSSMRA80A01H501Z" className="mt-1 bg-white font-mono" maxLength={16} />
            </div>
            <div>
              <Label className="text-xs text-stone-600">Indirizzo di residenza</Label>
              <Input value={form.indirizzo} onChange={e => setForm({ ...form, indirizzo: e.target.value })} placeholder="Via Roma 1, 20100 Milano" className="mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-xs text-stone-600">IBAN</Label>
              <Input data-testid="owner-iban-input" value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value.toUpperCase() })} placeholder="IT60X0542811101000000123456" className="mt-1 bg-white font-mono" maxLength={34} />
            </div>

            <Separator />

            <div className="p-3 bg-stone-50 border border-stone-200 rounded-sm">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-xs font-medium text-stone-700">Sostituto d'Imposta</p>
                  <p className="text-[10px] text-stone-400 mt-0.5">Il PM trattiene e versa le tasse per conto del proprietario</p>
                </div>
                <Switch
                  data-testid="owner-sostituto-toggle"
                  checked={form.sostituto_imposta}
                  onCheckedChange={v => setForm({ ...form, sostituto_imposta: v, regime_fiscale: v ? (form.regime_fiscale || "cedolare_21") : "" })}
                />
              </div>
              {form.sostituto_imposta && (
                <div className="mt-3">
                  <Label className="text-xs text-stone-600">Regime Fiscale</Label>
                  <Select value={form.regime_fiscale || "cedolare_21"} onValueChange={v => setForm({ ...form, regime_fiscale: v })}>
                    <SelectTrigger data-testid="owner-regime-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cedolare_21">Cedolare Secca 21%</SelectItem>
                      <SelectItem value="cedolare_26">Cedolare Secca 26%</SelectItem>
                      <SelectItem value="irpef">Scaglioni IRPEF 2025</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <Button onClick={handleSave} data-testid="owner-save-button" className="w-full bg-stone-900 hover:bg-stone-800 text-white">
              {editing ? "Salva Modifiche" : "Crea Proprietario"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
